import React, { FC, MutableRefObject, ReactElement, useContext, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { LanguageContext, ReactState } from '../../../../React/Common/Contexts'
import CLanguageHandler from '../../../../NonReact/Languages/LanguageHandler'
import { SelectFilterOption } from '../AdminFilters/SelectFilter'
import {
    getCMEsAsSelectOptions,
    getDevicesFromPartition,
    getPartitionsForTelServer,
    getTelServersAsSelectOptions
} from '../../../../React/Common/API/ItemsGetters'
import { displayError } from '../../../../React/Common/Utils/Messages'
import { isStringNotEmpty, isVarNotEmpty } from '../../../../NonReact/utils/GlobalFunctions'
import FormRow from '../../../../React/Common/Components/FormRow'
import { ValidationErrors } from '../../../../React/Common/Components/ValidationErrors/ValidationErrors'
import { ComboBox } from '../../../../React/Common/Components/ComboBox/ComboBox'
import { useFormContext, UseFormMethods, useWatch } from '../../../../Vendor/react-hook-form/dist/index.ie11'
import { DEFAULT_CME_ID } from '../../../../React/AddUser/Constants'
import { ALL_VALUE, ANY_VALUE_STRING_DEFAULT } from '../../../../React/Common/Constants/DefaultValues'
import { CMEFields } from '../../../../NonReact/constants/ApiConstants'
import { CME } from '../../../../React/Common/Types/Types'
import { isBitmaskMatching } from '../../../../React/Common/Utils/BitwiseOperations'
import { CapabilityBitFlags } from '../../../../React/CMEAddModify/Constants'
import { SelectOption, SelectOptions } from '../../../../React/Common/Components/Selects/Select/Select'
import { DeviceSelectItem } from '../../../../React/Common/Components/Selects/Types'
import { EMPTY_STRING } from '../../../../NonReact/constants/Common'
import { GenericScopeTab } from '../../../../../i18n'
import { GenericScopeAssignment } from './Types'

export type FieldNames = {
    cmeId: string
    telSysId: string
    partitionId: string
    deviceId: string
}

export type EnabledFlags = Partial<{
    cme: boolean
    telSys: boolean
    partition: boolean
    device: boolean
}>

export interface GenericConfigurationScopeProps {
    isViewOnly?: boolean
    fieldNames: FieldNames
    enabled?: EnabledFlags
}

export const GenericScopeAssignmentTab: FC<GenericConfigurationScopeProps> = ({
    isViewOnly = false,
    fieldNames,
    enabled = { cme: true, telSys: true, partition: true, device: true }
}: GenericConfigurationScopeProps): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle(GenericScopeTab.NAME)
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()
    const { setValue }: UseFormMethods<GenericScopeAssignment> = useFormContext()

    const [isCMELoading, setIsCMELoading]: ReactState<boolean> = useState(false)
    const [isTelSysLoading, setIsTelSysLoading]: ReactState<boolean> = useState(false)
    const [isPartitionLoading, setIsPartitionLoading]: ReactState<boolean> = useState(false)
    const [isDeviceLoading, setIsDeviceLoading]: ReactState<boolean> = useState(false)

    const [cmesOpts, setCmesOpts]: ReactState<SelectFilterOption[]> = useState<SelectFilterOption[]>([])
    const [telephonyServerOpts, setTelephonyServerOpts]: ReactState<SelectFilterOption[]> = useState<
        SelectFilterOption[]
    >([])
    const [partitionOpts, setPartitionOpts]: ReactState<SelectFilterOption[]> = useState<SelectFilterOption[]>([])
    const [deviceOpts, setDeviceOpts]: ReactState<SelectFilterOption[]> = useState<SelectFilterOption[]>([])

    const cmeValue: string = useWatch<string>({
        name: fieldNames.cmeId,
        defaultValue: DEFAULT_CME_ID
    })
    const telSysValue: number = useWatch<number>({
        name: fieldNames.telSysId,
        defaultValue: ALL_VALUE
    })
    const partitionValue: number = useWatch<number>({
        name: fieldNames.partitionId,
        defaultValue: ALL_VALUE
    })

    const getCMEs = (): void => {
        setIsCMELoading(true)

        getCMEsAsSelectOptions(
            [CMEFields.CAPABILITIES],
            (cme: CME): boolean =>
                isBitmaskMatching(cme.Capabilities ?? 0, CapabilityBitFlags.CXI_BROADWORKS) ||
                isBitmaskMatching(cme.Capabilities ?? 0, CapabilityBitFlags.CXI_WEBEX) ||
                isBitmaskMatching(cme.Capabilities ?? 0, CapabilityBitFlags.CXI_TEAMS)
        )
            .then((cmes: SelectOptions<string>): void => {
                if (isMounted.current) {
                    setCmesOpts(
                        cmes.map(
                            ({ label, value }: SelectOption<string>): SelectFilterOption => ({
                                label,
                                value
                            })
                        )
                    )
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) {
                    setIsCMELoading(false)
                }
            })
    }

    const getTelServers = (cmeID: string): void => {
        setIsTelSysLoading(true)
        getTelServersAsSelectOptions(cmeID, false, false)
            .then((telSyss: SelectOptions<number>): void => {
                if (isMounted.current) {
                    setTelephonyServerOpts([
                        ...telSyss.map(
                            ({ label, value }: SelectOption<number>): SelectFilterOption => ({
                                label,
                                value
                            })
                        )
                    ])
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) {
                    setIsTelSysLoading(false)
                }
            })
    }

    const getPartitions = (telSys: number): void => {
        setIsPartitionLoading(true)
        getPartitionsForTelServer(telSys)
            .then((partitions: SelectOptions<number>): void => {
                if (isMounted.current) {
                    setPartitionOpts([
                        ...partitions.map(
                            ({ label, value }: SelectOption<number>): SelectFilterOption => ({
                                label,
                                value
                            })
                        )
                    ])
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) {
                    setIsPartitionLoading(false)
                }
            })
    }

    const getDevices = (telsysID: number, paritionID: number): void => {
        setIsDeviceLoading(true)

        getDevicesFromPartition(telsysID, paritionID, false)
            .then(([devices]: [DeviceSelectItem[], Definable<number>]): void => {
                if (isMounted.current) {
                    setDeviceOpts([
                        ...devices.map(
                            ({ label, value }: DeviceSelectItem): SelectFilterOption => ({
                                label,
                                value
                            })
                        )
                    ])
                }
            })
            .catch(displayError)
            .finally((): void => {
                if (isMounted.current) {
                    setIsDeviceLoading(false)
                }
            })
    }

    useEffect((): void => {
        if (isStringNotEmpty(cmeValue)) {
            getTelServers(cmeValue)
        }
    }, [cmeValue])

    useEffect((): void => {
        if (isVarNotEmpty(telSysValue)) {
            if (telSysValue === ALL_VALUE) {
                setPartitionOpts([])
                setIsCMELoading(false)
            } else {
                getPartitions(telSysValue)
            }
        }
    }, [telSysValue])

    useEffect((): void => {
        if (isVarNotEmpty(telSysValue) && isVarNotEmpty(partitionValue)) {
            if (telSysValue === ALL_VALUE || partitionValue === ALL_VALUE) {
                setDeviceOpts([])
                setIsCMELoading(false)
            } else {
                getDevices(telSysValue, partitionValue)
            }
        }
    }, [telSysValue, partitionValue])

    useEffect(getCMEs, [])

    return (
        <div className='generic-scope-tab'>
            {enabled.cme && isVarNotEmpty(cmesOpts) ? (
                <FormRow>
                    <ValidationErrors name={fieldNames.cmeId}>
                        <ComboBox
                            name={fieldNames.cmeId}
                            defaultOption={cmesOpts?.[0] ?? { label: EMPTY_STRING, value: DEFAULT_CME_ID }}
                            options={cmesOpts.slice(1, cmesOpts.length)}
                            label={language.get(GenericScopeTab.GenericScopeTab_CME)}
                            variant='outlined'
                            onChange={(): void => {
                                setValue(fieldNames.telSysId, ALL_VALUE)
                                setValue(fieldNames.partitionId, ALL_VALUE)
                                setValue(fieldNames.deviceId, ANY_VALUE_STRING_DEFAULT)
                            }}
                            loading={isCMELoading}
                            disabled={isViewOnly}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {enabled.telSys && isVarNotEmpty(telephonyServerOpts) ? (
                <FormRow>
                    <ValidationErrors name={fieldNames.telSysId}>
                        <ComboBox
                            name={fieldNames.telSysId}
                            defaultOption={{ label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE }}
                            options={telephonyServerOpts}
                            label={language.get(GenericScopeTab.GenericScopeTab_Telsys)}
                            variant='outlined'
                            rules={{
                                validate: {
                                    isValid: (value: number): Definable<string> => {
                                        if (value === ALL_VALUE) {
                                            return language.get(GenericScopeTab.GenericScopeTab_Telsys_Required)
                                        }

                                        return undefined
                                    }
                                }
                            }}
                            onChange={(): void => {
                                setValue(fieldNames.partitionId, ALL_VALUE)
                                setValue(fieldNames.deviceId, ANY_VALUE_STRING_DEFAULT)
                            }}
                            loading={isTelSysLoading}
                            disabled={isViewOnly}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {enabled.partition && isVarNotEmpty(partitionOpts) ? (
                <FormRow>
                    <ValidationErrors name={fieldNames.partitionId}>
                        <ComboBox
                            name={fieldNames.partitionId}
                            defaultOption={{ label: ANY_VALUE_STRING_DEFAULT, value: ALL_VALUE }}
                            options={partitionOpts}
                            label={language.get(GenericScopeTab.GenericScopeTab_Partition)}
                            variant='outlined'
                            onChange={(): void => {
                                setValue(fieldNames.deviceId, ANY_VALUE_STRING_DEFAULT)
                            }}
                            loading={isPartitionLoading}
                            disabled={isViewOnly}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
            {enabled.device && isVarNotEmpty(deviceOpts) ? (
                <FormRow>
                    <ValidationErrors name={fieldNames.deviceId}>
                        <ComboBox
                            name={fieldNames.deviceId}
                            defaultOption={{
                                label: ANY_VALUE_STRING_DEFAULT,
                                value: ANY_VALUE_STRING_DEFAULT
                            }}
                            options={deviceOpts}
                            label={language.get(GenericScopeTab.GenericScopeTab_Device)}
                            variant='outlined'
                            loading={isDeviceLoading}
                            disabled={isViewOnly}
                        />
                    </ValidationErrors>
                </FormRow>
            ) : (
                <></>
            )}
        </div>
    )
}
